import { useEffect, useMemo, useState } from 'preact/hooks';
import { getAdminToken, getAccountEmail } from '../../utils/access';
import { CARS } from '../../cars/registry';
import { Wordmark } from '../components/Wordmark';

/**
 * Admin — KPIs + all-time cumulative curves (users, money, usage) + tables.
 * Only reachable signed in with an admin email (server-minted token).
 */
interface AdminUser {
  email: string;
  created_at: string;
  tesla_model: string | null;
  devices: number;
  purchases: number;
  cents: number;
  cars: string[] | null;
  laps: number;
  last_lap: string | null;
}
interface AdminPurchase {
  created_at: string;
  car_id: string;
  amount_cents: number | null;
  email: string | null;
  device_id: string;
  paid: boolean;
}
interface Stats {
  admin: string;
  users: AdminUser[];
  purchases: AdminPurchase[];
  devices: number;
  anon_buyers: number;
}
interface Payout {
  amount: number;
  currency: string;
  status: string;
  arrival: number | null;
  created: number | null;
  bank: Bank | null;
}
interface Bank { last4: string | null; name: string | null; country: string | null; currency: string | null }
interface SourceRow { src: string; opened: number; paid: number; cents: number }
interface Sources { opened: number; paid: number; since: number; rows: SourceRow[]; error?: string }
interface Payouts {
  available: number;
  pending: number;
  currency: string;
  livemode: boolean;
  account: { id: string | null; country: string | null; name: string | null; email: string | null; payouts_enabled: boolean; charges_enabled: boolean };
  schedule: { interval: string | null; delay_days: number | null; weekly_anchor: string | null; monthly_anchor: number | null };
  bank: Bank | null;
  payouts: Payout[];
  error?: string;
}

const eur = (cents: number) => (cents / 100).toLocaleString('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: cents % 100 ? 2 : 0 });
const carName = (id: string) => {
  const c = CARS.find((x) => x.id === id);
  return c ? `${c.manufacturer} ${c.name}` : id;
};
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');
const fmtDay = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : null);
/** Stripe reports in whatever currency the balance sits in, so never hard-code the euro here. */
const money = (cents: number, cur: string) =>
  (cents / 100).toLocaleString('en-GB', { style: 'currency', currency: (cur || 'eur').toUpperCase(), maximumFractionDigits: cents % 100 ? 2 : 0 });
const bankLine = (b: Bank | null) => (b ? [b.name, b.country, b.last4 ? `\u2022\u2022\u2022\u2022 ${b.last4}` : null].filter(Boolean).join(' \u00b7 ') : null);
/** Stripe's own words for a payout, in ours. */
const payoutWord = (s: string) =>
  ({ paid: 'in the bank', in_transit: 'on the way', pending: 'scheduled', failed: 'failed', canceled: 'cancelled' } as Record<string, string>)[s] ?? s;
/** "every week on friday, 7 days after each sale" from the account's payout schedule. */
function scheduleLine(p: Payouts): string {
  const s = p.schedule;
  const delay = s.delay_days != null ? `, ${s.delay_days} day${s.delay_days === 1 ? '' : 's'} after each sale clears` : '';
  if (s.interval === 'manual') return 'manual payouts: nothing leaves Stripe until you ask it to';
  if (s.interval === 'daily') return `paid out every day${delay}`;
  if (s.interval === 'weekly') return `paid out every week${s.weekly_anchor ? ` on ${s.weekly_anchor}` : ''}${delay}`;
  if (s.interval === 'monthly') return `paid out monthly${s.monthly_anchor ? ` on day ${s.monthly_anchor}` : ''}${delay}`;
  return 'payout schedule not set yet';
}
const DAY = 24 * 3600 * 1000;

interface Pt { t: number; v: number }

/** Cumulative series from event timestamps (+ optional weights). */
function cumulative(events: { t: number; w: number }[]): Pt[] {
  const sorted = [...events].sort((a, b) => a.t - b.t);
  const out: Pt[] = [];
  let acc = 0;
  for (const e of sorted) {
    acc += e.w;
    out.push({ t: e.t, v: acc });
  }
  return out;
}

/** "↘ 10.4% vs prev 24h" style delta. */
function delta24(times: number[]): { pct: number | null; cur: number; prev: number } {
  const now = Date.now();
  const cur = times.filter((t) => now - t < DAY).length;
  const prev = times.filter((t) => now - t >= DAY && now - t < 2 * DAY).length;
  const pct = prev === 0 ? (cur === 0 ? null : 100) : ((cur - prev) / prev) * 100;
  return { pct, cur, prev };
}

function Delta({ d, label }: { d: { pct: number | null }; label: string }) {
  if (d.pct == null) return <div class="adm__delta adm__delta--flat"><b>—</b><small>{label}</small></div>;
  const up = d.pct >= 0;
  return (
    <div class={`adm__delta ${up ? 'adm__delta--up' : 'adm__delta--down'}`}>
      <b>{up ? '↗' : '↘'} {Math.abs(d.pct).toFixed(1)}%</b>
      <small>{label}</small>
    </div>
  );
}

function CumulativeChart({ points, color, fmt, title, sub, minMax = 4 }: { points: Pt[]; color: string; fmt: (v: number) => string; title: string; sub: string; minMax?: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 560, H = 300, padL = 64, padR = 16, padT = 18, padB = 26;
  const now = Date.now();
  // x from first event (or 30 days ago when empty) to now; y from 0 to a nice max
  const t0 = points.length ? Math.min(points[0].t, now - 7 * DAY) : now - 30 * DAY;
  const t1 = now;
  const vmax = points.length ? points[points.length - 1].v : 0;
  const mag = vmax > 0 ? Math.pow(10, Math.floor(Math.log10(vmax))) : 1;
  const niceMax = Math.max(minMax, vmax <= 0 ? minMax : mag * Math.ceil(vmax / mag));
  const x = (t: number) => padL + ((t - t0) / Math.max(1, t1 - t0)) * (W - padL - padR);
  const y = (v: number) => padT + (H - padT - padB) * (1 - v / niceMax);
  const ticks = [0.25, 0.5, 0.75, 1].map((f) => niceMax * f);
  // step path (cumulative counts are steps), extended to "now"
  let d = '';
  if (points.length) {
    d = `M ${x(points[0].t)} ${y(0)} `;
    let prevV = 0;
    for (const p of points) {
      d += `L ${x(p.t)} ${y(prevV)} L ${x(p.t)} ${y(p.v)} `;
      prevV = p.v;
    }
    d += `L ${x(t1)} ${y(prevV)}`;
  }
  const h = hover != null ? points[hover] : null;
  const onMove = (e: MouseEvent) => {
    if (!points.length) return;
    const svg = e.currentTarget as SVGSVGElement;
    const r = svg.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    let best = 0, bd = Infinity;
    points.forEach((p, i) => { const dd = Math.abs(x(p.t) - px); if (dd < bd) { bd = dd; best = i; } });
    setHover(best);
  };
  return (
    <div class="adm__panel adm__chartpanel">
      <div class="adm__charthead"><span class="adm__eyebrow">{title}</span><span class="adm__chartsub">{sub}</span></div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} class="adm__grid" />
            <text x={padL - 10} y={y(t) + 4} class="adm__tick" text-anchor="end">{fmt(t)}</text>
          </g>
        ))}
        <line x1={padL} x2={W - padR} y1={y(0)} y2={y(0)} class="adm__grid adm__grid--base" />
        {d && <path d={`${d} L ${x(t1)} ${y(0)} Z`} fill={color} opacity="0.08" />}
        {d && <path d={d} fill="none" stroke={color} stroke-width="2" stroke-linejoin="round" />}
        {!points.length && <circle cx={x(t1) - 40} cy={y(0)} r="4" fill="#ffb020" />}
        {h && (
          <g>
            <line x1={x(h.t)} x2={x(h.t)} y1={padT} y2={y(0)} class="adm__cross" />
            <circle cx={x(h.t)} cy={y(h.v)} r="4.5" fill={color} stroke="#101216" stroke-width="2" />
            <g transform={`translate(${Math.min(W - 170, Math.max(padL, x(h.t) - 80))}, ${padT})`}>
              <rect width="160" height="40" rx="6" class="adm__tip" />
              <text x="10" y="16" class="adm__tiptxt">{new Date(h.t).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</text>
              <text x="10" y="32" class="adm__tiptxt adm__tiptxt--b">{fmt(h.v)}</text>
            </g>
          </g>
        )}
        <text x={padL} y={H - 6} class="adm__tick">{new Date(t0).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</text>
        <text x={W - padR} y={H - 6} class="adm__tick" text-anchor="end">today</text>
      </svg>
    </div>
  );
}

export function Admin() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [pay, setPay] = useState<Payouts | null>(null);
  const [src, setSrc] = useState<Sources | null>(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'users' | 'purchases'>('users');
  // the announcement: count first, send second
  const [annCar, setAnnCar] = useState(CARS.find((c) => c.available && !c.adminOnly)?.id ?? '');
  const [annBusy, setAnnBusy] = useState(false);
  const [annCount, setAnnCount] = useState(0);
  const [annNote, setAnnNote] = useState('');
  const [annKind, setAnnKind] = useState<'engine' | 'update' | 'promo'>('engine');
  const [annTitle, setAnnTitle] = useState('');
  const [annBody, setAnnBody] = useState('');
  const [annAudience, setAnnAudience] = useState<'all' | 'buyers' | 'others'>('all');
  // a promo is read before it is sent: the server renders it and it opens in a tab, written to nobody
  const previewPromo = async () => {
    try {
      const r = await fetch('/api/admin/announce', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken() ?? ''}` },
        body: JSON.stringify({ kind: 'promo', preview: true, title: annTitle, body: annBody }),
      });
      const j = (await r.json()) as { html?: string; error?: string };
      if (j.error || !j.html) { setAnnNote(j.error ?? 'no preview'); return; }
      const w = window.open('', '_blank');
      if (w) { w.document.open(); w.document.write(j.html); w.document.close(); }
    } catch (e) {
      setAnnNote(e instanceof Error ? e.message : String(e));
    }
  };
  const announce = async (send: boolean) => {
    setAnnBusy(true);
    setAnnNote('');
    try {
      const r = await fetch('/api/admin/announce', {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${getAdminToken() ?? ''}` },
        body: JSON.stringify({ carId: annCar, send, kind: annKind, title: annTitle, body: annBody, audience: annAudience }),
      });
      const j = (await r.json()) as { total?: number; buyers?: number; sent?: number; failed?: number; error?: string };
      if (j.error) setAnnNote(j.error);
      else if (send) { setAnnNote(`sent ${j.sent} · failed ${j.failed}`); setAnnCount(0); }
      else { setAnnCount(j.total ?? 0); setAnnNote(`${j.total} people · ${j.buyers} of them bought`); }
    } catch (e) {
      setAnnNote(e instanceof Error ? e.message : String(e));
    }
    setAnnBusy(false);
  };
  // the real mail, to the admin's own inbox, and to nobody else
  const testPromo = async () => {
    setAnnBusy(true);
    setAnnNote('');
    try {
      const r = await fetch('/api/admin/announce', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken() ?? ''}` },
        body: JSON.stringify({ kind: 'promo', title: annTitle, body: annBody, testTo: getAccountEmail() ?? '' }),
      });
      const j = (await r.json()) as { sent?: number; to?: string; error?: string };
      setAnnNote(j.error ? j.error : j.sent ? `test sent to ${j.to}` : 'test failed');
    } catch (e) {
      setAnnNote(e instanceof Error ? e.message : String(e));
    }
    setAnnBusy(false);
  };

  const load = async () => {
    setError('');
    try {
      const r = await fetch('/api/admin/stats', { headers: { Authorization: `Bearer ${getAdminToken() ?? ''}` } });
      if (r.status === 401) return setError('Not authorised. Sign in again with the admin email.');
      if (!r.ok) return setError(`Server error ${r.status}`);
      setStats((await r.json()) as Stats);
    } catch {
      setError('Network error');
    }
    // The money side is a separate ask: Stripe can be slow or unconfigured, and that must never take
    // the rest of the board down with it.
    try {
      const r = await fetch('/api/admin/payouts', { headers: { Authorization: `Bearer ${getAdminToken() ?? ''}` } });
      setPay((await r.json()) as Payouts);
    } catch {
      setPay({ error: 'Network error' } as Payouts);
    }
    try {
      const r = await fetch('/api/admin/sources', { headers: { Authorization: `Bearer ${getAdminToken() ?? ''}` } });
      setSrc((await r.json()) as Sources);
    } catch {
      setSrc({ error: 'Network error' } as unknown as Sources);
    }
  };
  useEffect(() => { void load(); }, []);

  const m = useMemo(() => {
    if (!stats) return null;
    const userTimes = stats.users.map((u) => new Date(u.created_at).getTime());
    const payTimes = stats.purchases.map((p) => new Date(p.created_at).getTime());
    const now = new Date();
    const total = stats.purchases.reduce((a, p) => a + (p.amount_cents ?? 0), 0);
    const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const payToday = stats.purchases.filter((p) => new Date(p.created_at).getTime() >= today0);
    return {
      usersCum: cumulative(userTimes.map((t) => ({ t, w: 1 }))),
      moneyCum: cumulative(stats.purchases.map((p) => ({ t: new Date(p.created_at).getTime(), w: p.amount_cents ?? 0 }))),
      usersDelta: delta24(userTimes),
      payDelta: delta24(payTimes),
      usersToday: userTimes.filter((t) => t >= today0).length,
      total,
      todayCents: payToday.reduce((a, p) => a + (p.amount_cents ?? 0), 0),
      todayCount: payToday.length,
      payers: new Set(stats.purchases.map((p) => p.email ?? p.device_id)).size,
    };
  }, [stats]);

  // the payout that has not landed yet: the one worth waiting for
  const nextPayout = pay && !pay.error ? pay.payouts.find((p) => p.status === 'pending' || p.status === 'in_transit') ?? null : null;

  return (
    <main class="adm">
      <header class="adm__bar">
        <a class="adm__brand" href="/">
          <img class="brandmark brandmark--h" src="/logo.svg?v=1" alt="" />
          <Wordmark class="adm__wordmark" />
          <span class="adm__tag">Immersive engines for your car</span>
        </a>
        <span class="adm__eyebrow adm__eyebrow--accent">Control room</span>
        <div class="adm__who">
          <span class="adm__dot" /><span class="adm__email">{getAccountEmail()}</span>
          <button class="adm__refresh" onClick={() => void load()}>REFRESH</button>
        </div>
      </header>

      {error && <div class="adm__err">{error}</div>}
      {!stats && !error && <div class="adm__loading">Loading…</div>}

      {stats && m && (
        <>
          <section class="adm__board">
            <div class="adm__cell">
              <span class="adm__eyebrow">Users</span>
              <b class="adm__num">{stats.users.length.toLocaleString('en-GB')}</b>
              <span class="adm__line">{m.usersToday} today <Delta d={m.usersDelta} label="vs prev 24h" /></span>
            </div>
            <div class="adm__cell">
              <span class="adm__eyebrow">Money received</span>
              <b class="adm__num adm__num--accent">{eur(m.total)}</b>
              <span class="adm__line">{eur(m.todayCents)} today · {stats.purchases.length} {stats.purchases.length === 1 ? 'purchase' : 'purchases'} all time</span>
            </div>
            <div class="adm__cell">
              <span class="adm__eyebrow">Paying customers</span>
              <b class="adm__num">{m.payers}</b>
              <span class="adm__line">{m.todayCount} today <Delta d={m.payDelta} label="vs prev 24h" /></span>
            </div>
            <div class="adm__cell adm__cell--last">
              <span class="adm__eyebrow">Devices</span>
              <b class="adm__num">{stats.devices}</b>
              <span class="adm__line">signed in · {stats.anon_buyers} {stats.anon_buyers === 1 ? 'buyer' : 'buyers'} without account</span>
            </div>
          </section>

          <section class="adm__charts">
            <CumulativeChart points={m.usersCum} color="#f2f2f0" fmt={(v) => Math.round(v).toLocaleString('en-GB')} title="Users · all time" sub="cumulative sign-ups" />
            <CumulativeChart points={m.moneyCum} color="#ff3d1f" fmt={(v) => eur(Math.round(v))} title="Money received · all time" sub="cumulative Stripe revenue" minMax={2000} />
          </section>

          {/* Where the money actually is. Stripe's own balance, the bank account it lands in and the day
              it lands, so nobody has to go and open a dashboard to answer "when do I get paid". */}
          <section class="adm__panel">
            <div class="adm__panelhead">
              <span class="adm__eyebrow">Payouts · Stripe</span>
              <span class="adm__sub">
                {pay && !pay.error ? scheduleLine(pay) : 'the balance at Stripe and the bank it lands in'}
                {pay && !pay.error && !pay.livemode && <span class="adm__badge adm__badge--test">test mode</span>}
              </span>
            </div>
            {!pay && <span class="adm__moneysub">Asking Stripe…</span>}
            {pay && pay.error && <span class="adm__moneysub">Stripe says: {pay.error}</span>}
            {pay && !pay.error && (
              <>
                <div class="adm__money">
                  <div class="adm__moneycell">
                    <span class="adm__eyebrow">Available now</span>
                    <b class="adm__moneynum">{money(pay.available, pay.currency)}</b>
                    <span class="adm__moneysub">cleared, waiting for the next payout</span>
                  </div>
                  <div class="adm__moneycell">
                    <span class="adm__eyebrow">Still clearing</span>
                    <b class="adm__moneynum">{money(pay.pending, pay.currency)}</b>
                    <span class="adm__moneysub">paid by the buyer, not released by Stripe yet</span>
                  </div>
                  <div class="adm__moneycell">
                    <span class="adm__eyebrow">Next in the bank</span>
                    <b class="adm__moneynum">{nextPayout ? fmtDay(nextPayout.arrival) ?? 'soon' : 'not yet'}</b>
                    <span class="adm__moneysub">{nextPayout ? `${money(nextPayout.amount, nextPayout.currency)} · ${payoutWord(nextPayout.status)}` : 'nothing on its way right now'}</span>
                  </div>
                  <div class="adm__moneycell">
                    <span class="adm__eyebrow">Lands in</span>
                    <b class="adm__moneynum adm__moneynum--sm">{bankLine(pay.bank) ?? 'no bank on file'}</b>
                    <span class="adm__moneysub">{[pay.account.name ?? pay.account.email ?? pay.account.id, pay.account.payouts_enabled ? null : 'payouts blocked'].filter(Boolean).join(' · ')}</span>
                  </div>
                </div>
                {pay.payouts.length > 0 && (
                  <div class="adm__paylist">
                    {pay.payouts.map((p, i) => (
                      <div class="adm__payrow" key={i}>
                        <b>{money(p.amount, p.currency)}</b>
                        <span>{fmtDay(p.arrival) ?? fmtDay(p.created) ?? ''}</span>
                        <span class={`adm__badge${p.status === 'paid' ? ' adm__badge--paid' : ''}`}>{payoutWord(p.status)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>

          {/* Where they came from. The page remembers the link that brought a browser here on its first
              visit and hands it to Stripe with the checkout, so the origin travels with the sale.
              Untagged rows are the ones opened before this shipped, and the referrer that apps strip. */}
          <section class="adm__panel">
            <div class="adm__panelhead">
              <span class="adm__eyebrow">Where they came from</span>
              <span class="adm__sub">
                {src && !src.error && src.opened > 0
                  ? `${src.paid} of the last ${src.opened} checkouts were paid · ${Math.round((src.paid / src.opened) * 100)}% finished`
                  : 'the link that brought each buyer, kept with the checkout'}
              </span>
            </div>
            {src && src.error && <span class="adm__moneysub">Stripe says: {src.error}</span>}
            {src && !src.error && src.rows.length === 0 && <span class="adm__moneysub">No checkout has been opened yet.</span>}
            {src && !src.error && src.rows.length > 0 && (
              <div class="adm__tablewrap adm__tablewrap--tight">
                <table class="adm__table adm__table--tight">
                  <thead><tr><th>Source</th><th class="num">Checkouts</th><th class="num">Bought</th><th class="num">Revenue</th></tr></thead>
                  <tbody>
                    {src.rows.map((r) => (
                      <tr key={r.src}>
                        <td data-l="Source">{r.src === 'not tagged' ? <span class="adm__muted">not tagged</span> : r.src}</td>
                        <td class="num" data-l="Checkouts">{r.opened}</td>
                        <td class="num" data-l="Bought">{r.paid}</td>
                        <td class="num" data-l="Revenue">{r.cents ? eur(r.cents) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* The one e-mail that is not transactional. It says who it would write to before it writes to
              anyone, it only goes to buyers and to those who ticked the box, and it carries a way out. */}
          <section class="adm__panel">
            <div class="adm__panelhead">
              <div class="adm__seg">
                <button class={annKind === 'engine' ? 'on' : ''} onClick={() => { setAnnKind('engine'); setAnnCount(0); setAnnNote(''); }}>New engine</button>
                <button class={annKind === 'promo' ? 'on' : ''} onClick={() => { setAnnKind('promo'); setAnnCount(0); setAnnNote(''); }}>Promo</button>
                <button class={annKind === 'update' ? 'on' : ''} onClick={() => { setAnnKind('update'); setAnnCount(0); setAnnNote(''); }}>Service notice</button>
              </div>
              <span class="adm__sub">
                {annKind === 'update'
                  ? 'not commercial · every account · a change to the service, not an offer'
                  : annKind === 'promo'
                  ? 'commercial, in your words · buyers + everyone who asked to be told · never anyone who unsubscribed'
                  : 'commercial · buyers + everyone who asked to be told · never anyone who unsubscribed'}
              </span>
            </div>
            <div class="adm__announce">
              {annKind === 'engine' ? (
                <select value={annCar} onChange={(e) => setAnnCar((e.currentTarget as HTMLSelectElement).value)}>
                  {CARS.filter((c) => c.available && !c.adminOnly).map((c) => <option key={c.id} value={c.id}>{c.manufacturer} {c.name}</option>)}
                </select>
              ) : (
                <input class="adm__input" placeholder={annKind === 'promo' ? 'Subject · e.g. Three new engines, and a week to drive them' : 'Subject · e.g. The wheel now changes gear'} value={annTitle} onInput={(e) => setAnnTitle((e.currentTarget as HTMLInputElement).value)} />
              )}
              <button class="adm__btn" disabled={annBusy} onClick={() => void announce(false)}>{annBusy ? 'Counting…' : 'Who would get it'}</button>
              {annKind === 'promo' && (
                <select value={annAudience} onChange={(e) => { setAnnAudience((e.currentTarget as HTMLSelectElement).value as 'all' | 'buyers' | 'others'); setAnnCount(0); setAnnNote(''); }}>
                  <option value="all">everyone on the list</option>
                  <option value="buyers">only people who bought</option>
                  <option value="others">only people who did not buy</option>
                </select>
              )}
              {annKind === 'promo' && <button class="adm__btn" disabled={annTitle.trim().length < 3 || !annBody.trim()} onClick={() => void previewPromo()}>Preview</button>}
              {annKind === 'promo' && <button class="adm__btn" disabled={annBusy || annTitle.trim().length < 3 || !annBody.trim()} onClick={() => void testPromo()}>Send me a test</button>}
              <button class="adm__btn adm__btn--go" disabled={annBusy || !annCount || (annKind !== 'engine' && annTitle.trim().length < 3) || (annKind === 'promo' && !annBody.trim())} onClick={() => { if (confirm(`Send this to ${annCount} people? It cannot be taken back.`)) void announce(true); }}>Send it</button>
              {annNote && <span class="adm__announcenote">{annNote}</span>}
              {annKind !== 'engine' && (
                <textarea class="adm__textarea" rows={annKind === 'promo' ? 10 : 4} placeholder={annKind === 'promo' ? 'The offer, in your words. Blank line = new paragraph · "## " = a heading · "- " = a bullet · **bold**' : 'What changed, in your own words. One blank line starts a new paragraph.'} value={annBody} onInput={(e) => setAnnBody((e.currentTarget as HTMLTextAreaElement).value)} />
              )}
            </div>
          </section>

          <section class="adm__panel">
            <div class="adm__panelhead">
              <div class="adm__seg">
                <button class={tab === 'users' ? 'on' : ''} onClick={() => setTab('users')}>Users · {stats.users.length}</button>
                <button class={tab === 'purchases' ? 'on' : ''} onClick={() => setTab('purchases')}>Purchases · {stats.purchases.length}</button>
              </div>
            </div>
            <div class="adm__tablewrap">
              {tab === 'users' ? (
                <table class="adm__table">
                  <thead><tr><th>Email</th><th>Joined</th><th>Tesla</th><th>Cars</th><th class="num">Devices</th><th class="num">Laps</th><th>Last lap</th><th class="num">Spent</th></tr></thead>
                  <tbody>
                    {stats.users.length === 0 && <tr><td colSpan={8} class="adm__empty">No accounts yet.</td></tr>}
                    {stats.users.map((u) => (
                      <tr key={u.email}>
                        <td data-l="Email">{u.email}</td>
                        <td data-l="Joined">{fmtDate(u.created_at)}</td>
                        <td data-l="Tesla">{u.tesla_model ?? '—'}</td>
                        <td data-l="Cars">{u.cars && u.cars.length ? u.cars.map(carName).join(', ') : <span class="adm__muted">demo only</span>}</td>
                        <td class="num" data-l="Devices">{u.devices}</td>
                        <td class="num" data-l="Laps">{u.laps}</td>
                        <td data-l="Last lap">{fmtDate(u.last_lap)}</td>
                        <td class="num" data-l="Spent">{u.cents ? eur(u.cents) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table class="adm__table">
                  <thead><tr><th>Date</th><th>Car</th><th>Buyer</th><th class="num">Amount</th></tr></thead>
                  <tbody>
                    {stats.purchases.length === 0 && <tr><td colSpan={4} class="adm__empty">No purchases yet.</td></tr>}
                    {stats.purchases.map((p, i) => (
                      <tr key={i}>
                        <td data-l="Date">{new Date(p.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                        <td data-l="Car">{carName(p.car_id)}</td>
                        <td data-l="Buyer">{p.email ?? <span class="adm__muted">{p.device_id.slice(0, 14)}…</span>}</td>
                        <td class="num" data-l="Amount">{p.amount_cents != null ? eur(p.amount_cents) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </>
      )}
    </main>
  );
}
